import { Menu, popoverClasses } from "@mui/material";
import { useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { DeviationStamp } from "features/deviations";
import { DitioMachineStamp } from "features/ditio";
import { DitioChecklistStamp } from "features/ditio/stamps/checklist";
import { ClassificationStamp } from "features/pointVisualization/classificationStamp";
import { PropertiesStamp } from "features/properties";
import { LogPointStamp, MachineLocationStamp as XsiteMachineLocationStamp } from "features/xsiteManage";
import { selectIsProcoreDesign, selectIsProcoreMobileDesign } from "slices/explorer";

import { CanvasContextMenuStamp } from "./canvasContextMenuStamp";
import { ProcoreMobileContextMenuStamp } from "./canvasContextMenuStamp/procoreMobileStamp";
import { ProcoreCanvasContextMenuStamp } from "./canvasContextMenuStamp/procoreStamp";
import { STAMP_MENU_PAPER_PROPS } from "./consts";
import { markMobileMenuDismissed } from "./mobileMenuDismissalFlag";
import { renderActions, selectStamp } from "./renderSlice";
import { StampKind } from "./types";

export function Stamp() {
    const dispatch = useAppDispatch();
    const stamp = useAppSelector(selectStamp);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const isProcoreMobileDesign = useAppSelector(selectIsProcoreMobileDesign);
    // Ref so the pointerdown handler always reads the latest stamp without
    // being added to the effect dependency array (which would re-register the
    // listener on every stamp position/data update).
    const stampRef = useRef(stamp);
    stampRef.current = stamp;
    const isProcoreMobileDesignRef = useRef(isProcoreMobileDesign);
    isProcoreMobileDesignRef.current = isProcoreMobileDesign;

    // Close pinned stamps (context menus) when the user clicks outside the menu system
    // or presses Escape. The Menu uses disableAutoFocus so it never receives keyboard
    // events; Escape has to be handled on document. The backdrop has pointer-events:none
    // so it can't handle outside clicks itself.
    // Checks for the data-stamp-menu attribute (set on the main paper and on NestedMenuItem's
    // content div) so only our specific menu tree is treated as "inside" — unrelated MUI
    // popovers elsewhere on the page are correctly treated as "outside".
    // Selection changes are handled by canvas click logic so UI interactions only dismiss the menu.
    useEffect(() => {
        if (!stamp?.pinned) {
            return;
        }

        const handlePointerDown = (e: PointerEvent) => {
            if (!isProcoreDesign) {
                return;
            }
            const target = e.target;
            if (!(target instanceof Element)) {
                return;
            }
            if (target.closest("[data-stamp-menu]")) {
                return;
            }
            if (isProcoreMobileDesignRef.current && stampRef.current?.kind === StampKind.CanvasContextMenu) {
                markMobileMenuDismissed();
            }
            dispatch(renderActions.setStamp(null));
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            // Same scope as pointer-down: standalone pinned stamps (e.g. Properties)
            // keep their existing behavior; only Procore context menus close here.
            if (!isProcoreDesign) {
                return;
            }
            if (e.key !== "Escape") {
                return;
            }
            if (isProcoreMobileDesignRef.current && stampRef.current?.kind === StampKind.CanvasContextMenu) {
                markMobileMenuDismissed();
            }
            dispatch(renderActions.setStamp(null));
        };

        document.addEventListener("pointerdown", handlePointerDown, true);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("pointerdown", handlePointerDown, true);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [stamp?.pinned, isProcoreDesign, dispatch]);

    if (!stamp) {
        return null;
    }

    return (
        <Menu
            open={true}
            onClose={() => dispatch(renderActions.setStamp(null))}
            disableEnforceFocus
            disableAutoFocus
            autoFocus={false}
            disableAutoFocusItem
            disableRestoreFocus
            PaperProps={STAMP_MENU_PAPER_PROPS}
            MenuListProps={{
                sx:
                    isProcoreMobileDesign && stamp.kind === StampKind.CanvasContextMenu
                        ? { padding: 0, overflow: "visible" }
                        : undefined,
            }}
            sx={{
                [`&.${popoverClasses.root}`]: {
                    pointerEvents: "none",
                    padding: 0,
                },
            }}
            anchorReference="anchorPosition"
            anchorPosition={{ top: stamp.mouseY + 8, left: stamp.mouseX + 24 }}
            transitionDuration={{ exit: 0, enter: 150 }}
        >
            {stamp.kind === StampKind.XsiteManageMachineLocation ? (
                <XsiteMachineLocationStamp />
            ) : stamp.kind === StampKind.LogPoint ? (
                <LogPointStamp />
            ) : stamp.kind === StampKind.Deviation ? (
                <DeviationStamp />
            ) : stamp.kind === StampKind.CanvasContextMenu ? (
                isProcoreDesign ? (
                    isProcoreMobileDesign ? (
                        <ProcoreMobileContextMenuStamp />
                    ) : (
                        <ProcoreCanvasContextMenuStamp />
                    )
                ) : (
                    <CanvasContextMenuStamp />
                )
            ) : stamp.kind === StampKind.Properties ? (
                <PropertiesStamp />
            ) : stamp.kind === StampKind.DitioMachine ? (
                <DitioMachineStamp />
            ) : stamp.kind === StampKind.DitioChecklist ? (
                <DitioChecklistStamp />
            ) : stamp.kind === StampKind.Classification ? (
                <ClassificationStamp />
            ) : null}
        </Menu>
    );
}
